/**
 * Memoir-prose template engine for the dossier ABOUT tab. Stitches the
 * persisted snapshot fields (DraftOffer + PartyMember / CareerEntry) +
 * hand-authored Gen-3 flavor (flavorText.ts) + @pkmn/dex species data
 * into 2 prose paragraphs + 1 italic pull-quote between them, plus an
 * optional epitaph paragraph for career-snapshot mode.
 *
 * Helper-split pattern per SR-8 + EN-12: each paragraph is a separate
 * pure synchronous helper, vitest-testable in Node. The Phaser shell at
 * PR 2's FullSheet rewrite handles the await-then-rerender cycle —
 * helpers receive null for `species` / `flavor` when the dex load is
 * still in flight and degrade gracefully (skip facts sentence, skip
 * pull-quote — SR-6 silent degradation pattern).
 *
 * `caughtMapDisplay` is pre-resolved by the caller via mapDisplayName()
 * from scenes/titleSlotData. Passing pre-resolved strings keeps this
 * module a pure-data layer with no cross-layer (scenes/) imports.
 *
 * Voice notes: spare, declarative sentences. Memoir-as-content (Slay
 * the Spire run-history cousin), NOT memoir-as-italic-Fraunces (banned
 * for chrome copy per DESIGN.md anti-slop — the single italic moment
 * is the flavor pull-quote, which is per-design italic at the CSS layer).
 */

import type { DraftOffer } from '../state/draftRoll';
import type { PartyMember } from '../state/runState';
import type { CareerEntry } from '../state/careerStore';
import type { SpeciesFlavor, GrowthRate } from './flavorText';

/**
 * FullSheet data discriminator per EN-4 / EN-7. Caller assembles the
 * union at modal-open time:
 *   - 'live'            : alive PartyMember in current run
 *   - 'career-snapshot' : drafted-then-died entry (caller chose this kind)
 *   - 'snapshot'        : skipped offer / pre-pick view / pre-v9 fallback
 */
export type FullSheetData =
  | { kind: 'live'; offer: DraftOffer; member: PartyMember }
  | { kind: 'career-snapshot'; offer: DraftOffer; entry: CareerEntry }
  | { kind: 'snapshot'; offer: DraftOffer };

/** Subset of @pkmn/dex's Species type that the prose helpers consume.
 *  Keeping the dependency minimal lets vitest mocks stay tiny. */
export interface DossierProseSpecies {
  weightkg?: number;
}

/**
 * Input to the prose helpers. All fields are PRE-RESOLVED by the caller
 * (FullSheet's create() runs the async loads + map-name lookup before
 * invoking these helpers). null fields signal "data unavailable, degrade
 * gracefully" per SR-6.
 */
export interface DossierProseInput {
  data: FullSheetData;
  /** Resolved from `(await loadGen3()).species.get(speciesId)`. */
  species: DossierProseSpecies | null;
  /** Resolved from `getFlavor(speciesId)` (flavorText.ts). */
  flavor: SpeciesFlavor | null;
  /** Resolved from `mapDisplayName(offer.caughtAt.mapName)`. null when
   *  the caughtAt sentinel is set (pre-v9 entry, mapName === '') OR the
   *  caller doesn't have a caught-where context. */
  caughtMapDisplay: string | null;
}

/** All four paragraph slots, bundled. Falsy slots are filtered out by
 *  the renderer in PR 2; the helpers always populate every slot. */
export interface ProseSections {
  intro: string;
  flavorPullQuote: string | null;
  facts: string;
  epitaph: string | null;
}

/** Convert raw inches to cartridge feet+inches notation (e.g. 28 → "2'04""). */
function cartridgeHeight(inches: number): string {
  const feet = Math.floor(inches / 12);
  const rem = inches % 12;
  return `${feet}'${rem.toString().padStart(2, '0')}"`;
}

/** Round kg → lb (cartridge NA convention). */
function kgToLbs(kg: number): number {
  return Math.round(kg * 2.20462);
}

/** "A" vs "An" article selector — matches the first letter's vowel sound.
 *  Among the growth-rate labels only "erratic" is vowel-starting; the
 *  helper stays generic for cases where other vowel-leading phrases land
 *  here later. */
function indefiniteArticle(word: string): 'A' | 'An' {
  return /^[aeiou]/i.test(word) ? 'An' : 'A';
}

/** Display label for cartridge growth rate enums. */
function growthRateLabel(g: GrowthRate): string {
  const labels: Record<GrowthRate, string> = {
    'slow':         'slow',
    'medium-slow':  'medium slow',
    'medium-fast':  'medium fast',
    'fast':         'fast',
    'erratic':      'erratic',
    'fluctuating':  'fluctuating',
  };
  return labels[g];
}

/**
 * Opening paragraph — identity + caught-where + draft level. Voice
 * varies by kind:
 *   - live + career-snapshot: declarative ("X was caught at Y at level N.")
 *   - snapshot (skipped):     counterfactual ("Would have been a X, ...")
 *
 * Pre-v9 entries (caughtMapDisplay === null) skip the caught-where
 * sentence entirely — short fallback "X." / "Would have been a X." per
 * design Q2 + EN-6 sentinel handling.
 */
export function composeIntroParagraph(input: DossierProseInput): string {
  const { data, caughtMapDisplay } = input;
  const name = data.offer.displayName;
  const level = data.offer.level;
  if (data.kind === 'snapshot') {
    if (caughtMapDisplay) {
      return `Would have been a ${name}, found at ${caughtMapDisplay} at level ${level}.`;
    }
    return `Would have been a ${name} at level ${level}.`;
  }
  // 'live' or 'career-snapshot' — was actually caught.
  if (caughtMapDisplay) {
    return `${name} was caught at ${caughtMapDisplay} at level ${level}.`;
  }
  // Pre-v9 fallback: drop the place entirely (skipping invalid render
  // of "Caught at  at level X").
  return `${name} was drafted at level ${level}.`;
}

/**
 * Italic flavor pull-quote — the cartridge dex entry's classic blurb.
 * Returns null when no hand-authored entry exists for the species
 * (content gap) OR when `flavor` itself is null (async resolve in
 * flight). PR 2's renderer skips the italic block entirely in both
 * cases.
 */
export function composeFlavorPullQuote(input: DossierProseInput): string | null {
  return input.flavor?.flavorText ?? null;
}

/**
 * Facts paragraph — height, weight, growth rate woven into one or two
 * short sentences. Empty string when every constituent field is null —
 * caller hides the paragraph in that case.
 *
 * Example outputs:
 *   - Full data:  "Stands 2'04\" tall, 15 lb. A medium slow grower."
 *   - Only height: "Stands 2'04\" tall."
 *   - Only weight: "Weighs 15 lb."
 *   - Only growth: "A medium slow grower."
 *   - All null:    ""
 */
export function composeFactsParagraph(input: DossierProseInput): string {
  const { species, flavor } = input;
  const physical: string[] = [];
  if (flavor?.heightInches) {
    physical.push(`Stands ${cartridgeHeight(flavor.heightInches)} tall`);
  }
  if (species?.weightkg) {
    // Tack onto the standing sentence when both fields are present;
    // otherwise lead with "Weighs N lb" as its own sentence.
    if (flavor?.heightInches) {
      physical.push(`${kgToLbs(species.weightkg)} lb`);
    } else {
      physical.push(`Weighs ${kgToLbs(species.weightkg)} lb`);
    }
  }
  const sentence1 = physical.length > 0 ? `${physical.join(', ')}.` : '';
  let sentence2 = '';
  if (flavor?.growthRate) {
    const label = growthRateLabel(flavor.growthRate);
    sentence2 = `${indefiniteArticle(label)} ${label} grower.`;
  }
  return [sentence1, sentence2].filter((s) => s.length > 0).join(' ');
}

/**
 * Closing paragraph for career-snapshot kind — death summary. Returns
 * null when:
 *   - kind is anything other than 'career-snapshot' (alive in this run
 *     or skipped entirely — no death to summarize).
 *   - The career entry's death fields are all null (defensive — DR-13/
 *     EN-6 invariant says RECENT DEATHS filter guarantees non-null but
 *     this helper doesn't trust that filter outside the FullSheet path).
 */
export function composeEpitaphParagraph(input: DossierProseInput): string | null {
  if (input.data.kind !== 'career-snapshot') return null;
  const { entry } = input.data;
  if (entry.diedTo === null || entry.diedZone === null || entry.levelAtDeath === null) {
    return null;
  }
  return `Fell to ${entry.diedTo} on ${entry.diedZone} at level ${entry.levelAtDeath}.`;
}

/**
 * Public entrypoint per SR-8. Bundles all four section calls; the
 * Phaser shell consumes the typed result and renders each non-empty
 * slot. Side-effect-free; safe to call on every async-resolve tick.
 */
export function composeDossierProse(input: DossierProseInput): ProseSections {
  return {
    intro: composeIntroParagraph(input),
    flavorPullQuote: composeFlavorPullQuote(input),
    facts: composeFactsParagraph(input),
    epitaph: composeEpitaphParagraph(input),
  };
}
